import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper
} from '@mui/material';
import {
  CheckCircle,
  MedicalServices,
  Person,
  CalendarMonth,
  ChevronRight
} from '@mui/icons-material';

// Step constants
const STEPS = {
  SERVICE: 1,
  PRACTITIONER: 2,
  DATETIME: 3,
  DETAILS: 4,
  PAYMENT: 5,
  CONFIRMATION: 6
};

const MobileBookingSummary = ({
  booking,
  currentStep,
  onStepClick
}) => {
  const items = [];

  // Service
  if (booking.service) {
    items.push({
      step: STEPS.SERVICE,
      icon: <MedicalServices sx={{ fontSize: 16 }} />,
      label: 'Service',
      value: booking.service.name,
      completed: currentStep > STEPS.SERVICE
    });
  }

  // Practitioner
  if (booking.practitioner) {
    items.push({
      step: STEPS.PRACTITIONER,
      icon: <Person sx={{ fontSize: 16 }} />,
      label: 'Practitioner',
      value: booking.practitioner.name,
      completed: currentStep > STEPS.PRACTITIONER
    });
  }

  // Date/Time
  if (booking.selectedTime) {
    items.push({
      step: STEPS.DATETIME,
      icon: <CalendarMonth sx={{ fontSize: 16 }} />,
      label: 'Time',
      value: booking.selectedTime.formatted || booking.selectedTime.time_display,
      completed: currentStep > STEPS.DATETIME
    });
  }

  if (items.length === 0) return null;

  return (
    <Paper
      sx={{
        position: 'sticky',
        top: 56, // Below header
        zIndex: 99,
        mx: 2,
        mt: 2,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 2
      }}
    >
      {/* Header */}
      <Box sx={{
        bgcolor: 'primary.main',
        color: 'white',
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" fontWeight="600">
          Your Booking
        </Typography>
        <Chip
          label={`Step ${currentStep} of 5`}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white',
            height: 22,
            fontSize: '0.7rem'
          }}
        />
      </Box>

      {/* Items */}
      <Box sx={{ p: 1.5 }}>
        {items.map((item, index) => (
          <Box
            key={item.step}
            onClick={() => item.completed && onStepClick(item.step)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
              px: 1,
              borderRadius: 1,
              cursor: item.completed ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              '&:hover': item.completed ? {
                bgcolor: 'grey.100'
              } : {},
              borderBottom: index < items.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider'
            }}
          >
            {/* Icon */}
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: item.completed ? 'success.light' : 'primary.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: item.completed ? 'success.main' : 'primary.main'
            }}>
              {item.completed ? (
                <CheckCircle sx={{ fontSize: 18 }} />
              ) : (
                item.icon
              )}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1 }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="500"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {item.value}
              </Typography>
            </Box>

            {/* Edit indicator */}
            {item.completed && (
              <ChevronRight
                sx={{ fontSize: 18, color: 'text.disabled' }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default MobileBookingSummary;
